import React, { useEffect, useState } from "react";
import { useActiveFBOs } from "../../../containers/ActiveFBOContainer";
import { useApi } from "../../../containers/ApiContainer";
import { PlacementService } from "../../../services/PlacementService";
import { TripWithTenant } from "../../../types";
import { AssignLocationDialogPresenter } from "./AssignLocationDialogPresenter";

interface AssignLocationDialogProps {
  open: boolean;
  trip: TripWithTenant | null;
  currentLocation?: string | null;
  onClose: () => void;
  onAssign: () => void;
}

interface LocationOption {
  id: string;
  name: string;
  type: "hangar" | "ramp";
}

export const AssignLocationDialog: React.FC<AssignLocationDialogProps> = ({
  open,
  trip,
  currentLocation,
  onClose,
  onAssign,
}) => {
  const { activeFBO } = useActiveFBOs();
  const { postgrest } = useApi();
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string>("");

  // Date range state
  const [useCustomDates, setUseCustomDates] = useState(false);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);

  const placementService = React.useMemo(
    () => new PlacementService(postgrest),
    [postgrest]
  );

  // Initialize dates when dialog opens
  useEffect(() => {
    if (!open || !trip) return;

    // Initialize date range to current time and trip departure (forward-fill)
    const now = new Date();
    const arrival = new Date(trip.arrival_date);
    const departure = new Date(trip.departure_date);

    // Start time is the later of now or arrival
    setStartTime(now > arrival ? now : arrival);
    setEndTime(departure);
    setUseCustomDates(false);
    setSelectedLocation(currentLocation || "unassigned");
  }, [open, trip, currentLocation]);

  // Load available locations
  useEffect(() => {
    const loadLocations = async () => {
      if (!open || !trip || !activeFBO) return;

      setLoading(true);
      setError(null);

      try {
        // Check availability for the custom date range if set
        const checkStartTime =
          useCustomDates && startTime
            ? startTime.toISOString()
            : trip.arrival_date;
        const checkEndTime =
          useCustomDates && endTime
            ? endTime.toISOString()
            : trip.departure_date;

        const availableLocations = await placementService.getAvailableLocations(
          checkStartTime,
          checkEndTime,
          activeFBO.id
        );

        setLocations(availableLocations);
      } catch (err: any) {
        console.error("Failed to load locations:", err);
        setError(err.message || "Failed to load available locations");
      } finally {
        setLoading(false);
      }
    };

    loadLocations();
  }, [
    open,
    trip,
    activeFBO,
    placementService,
    useCustomDates,
    startTime,
    endTime,
  ]);

  // Handle location assignment
  const handleAssign = async () => {
    if (!trip) {
      onClose();
      return;
    }

    setAssigning(true);
    setError(null);

    try {
      // Handle unassigned case
      if (selectedLocation === "unassigned") {
        await placementService.assignToLocationWithGapFilling(
          trip.trip_id,
          null,
          null,
          useCustomDates && startTime ? startTime.toISOString() : undefined,
          useCustomDates && endTime ? endTime.toISOString() : undefined,
          true // excludeConflicts = true (allow overlaps)
        );
      } else {
        // Find the selected location details
        const location = locations.find((l) => l.id === selectedLocation);
        if (!location) {
          throw new Error("Selected location not found");
        }

        // Use the stored procedure method for assignments
        // Note: We allow conflicts (excludeConflicts = true) so users can assign anywhere
        await placementService.assignToLocationWithGapFilling(
          trip.trip_id,
          location.id,
          location.type,
          useCustomDates && startTime ? startTime.toISOString() : undefined,
          useCustomDates && endTime ? endTime.toISOString() : undefined,
          true // excludeConflicts = true (allow overlaps)
        );
      }

      // Success! Close dialog and refresh
      onAssign();
      onClose();
    } catch (err: any) {
      console.error("Failed to assign location:", err);
      setError(err.message || "Failed to assign location");
    } finally {
      setAssigning(false);
    }
  };

  return (
    <AssignLocationDialogPresenter
      open={open}
      onClose={onClose}
      trip={trip}
      currentLocation={currentLocation}
      loading={loading}
      assigning={assigning}
      error={error}
      setError={setError}
      locations={locations}
      selectedLocation={selectedLocation}
      setSelectedLocation={setSelectedLocation}
      useCustomDates={useCustomDates}
      setUseCustomDates={setUseCustomDates}
      startTime={startTime}
      setStartTime={setStartTime}
      endTime={endTime}
      setEndTime={setEndTime}
      handleAssign={handleAssign}
    />
  );
};